import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import { useTheme, alpha } from "@mui/material/styles";

export interface InstructionStep {
    title: string;
    body: string;
    image?: string;
    imageAlt?: string;
}

interface InstructionsProps {
    title?: string;
    steps: InstructionStep[];
}

export default function Instructions({ title = "Setup Instructions", steps }: InstructionsProps) {
    const theme = useTheme();

    return (
        <Box
            sx={{
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.default,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    px: 3,
                    py: 2,
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    backgroundColor: theme.palette.background.paper,
                }}
            >
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        fontSize: "1.1rem",
                        color: "text.primary",
                    }}
                >
                    {title}
                </Typography>
            </Box>
            <Stack spacing={0} divider={null}>
                {steps.map((step, index) => (
                    <Box
                        key={index}
                        sx={{
                            px: 3,
                            py: 2.5,
                            borderBottom:
                                index < steps.length - 1
                                    ? `1px solid ${theme.palette.divider}`
                                    : "none",
                        }}
                    >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                            <Box
                                sx={{
                                    minWidth: 28,
                                    height: 28,
                                    borderRadius: "50%",
                                    backgroundColor: alpha(theme.palette.primary.main, 0.12),
                                    color: theme.palette.primary.main,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontWeight: 700,
                                    fontSize: "0.875rem",
                                }}
                            >
                                {index + 1}
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        fontWeight: 700,
                                        mb: 0.5,
                                        fontSize: "0.95rem",
                                    }}
                                >
                                    {step.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ lineHeight: 1.6 }}
                                >
                                    {step.body}
                                </Typography>
                                {step.image && (
                                    <Box
                                        component="img"
                                        src={step.image}
                                        alt={step.imageAlt ?? step.title}
                                        sx={{
                                            mt: 1.5,
                                            maxWidth: "100%",
                                            width: "auto",
                                            borderRadius: 1,
                                            border: `1px solid ${theme.palette.divider}`,
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    </Box>
                ))}
            </Stack>
        </Box>
    );
}
